/*
 * waitlist (page)
 */
import { appConfig } from "@emr/config"
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Divider,
	Page,
	toast,
} from "@emr/react-ui"
import { isOnWaitlist } from "@emr/utils"
import { usePushNotification } from "@emr/push-notifications"
import { createFileRoute } from "@tanstack/react-router"
import { Bell, BellRing, CheckCircle, Clock } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "../trpc"
import { authPageLoaderAuthenticated } from "../utils/auth.utils"

export const Route = createFileRoute("/waitlist")({
	component: WaitlistPage,
	loader: authPageLoaderAuthenticated(appConfig.redirects.afterSignInUrl),
})

function WaitlistPage() {
	const { t } = useTranslation()
	const { user } = Route.useLoaderData()
	const [onWaitlist, setOnWaitlist] = useState(() => isOnWaitlist(user))

	const addToWaitlist = trpc.user.addToWaitlist.useMutation({
		onSuccess: () => {
			setOnWaitlist(true)
			toast.success(t("waitlist.onList.addedToast"))
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	const removeFromWaitlist = trpc.user.removeFromWaitlist.useMutation({
		onSuccess: () => {
			setOnWaitlist(false)
			toast.success(t("waitlist.onList.removedToast"))
		},
		onError: (err) => {
			toast.error(err.message)
		},
	})

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8 pb-12 text-center"}>
				{onWaitlist ? (
					<>
						<CardHeader className={"items-center gap-3"}>
							<div
								className={
									"w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
								}
							>
								<CheckCircle className={"w-7 h-7 text-primary"} />
							</div>
							<CardTitle className={"text-2xl"}>
								{t("waitlist.onList.title")}
							</CardTitle>
							<CardDescription className={"text-base"}>
								{t("waitlist.onList.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className={"flex flex-col gap-4"}>
							<p className={"text-muted-foreground text-sm"}>
								{t("waitlist.onList.body")}
							</p>
							<p className={"text-xs text-muted-foreground"}>
								{t("waitlist.onList.noSpam")}{" "}
								<button
									className={
										"underline underline-offset-2 cursor-pointer disabled:opacity-50"
									}
									disabled={removeFromWaitlist.isPending}
									onClick={() => removeFromWaitlist.mutate()}
								>
									{removeFromWaitlist.isPending
										? t("waitlist.onList.removing")
										: t("waitlist.onList.removeMe")}
								</button>
							</p>
							<Divider />
							<WaitlistPushOption userId={user.id} />
						</CardContent>
					</>
				) : (
					<>
						<CardHeader className={"items-center gap-3"}>
							<div
								className={
									"w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center"
								}
							>
								<Clock className={"w-7 h-7 text-primary"} />
							</div>
							<CardTitle className={"text-2xl"}>
								{t("waitlist.join.title")}
							</CardTitle>
							<CardDescription className={"text-base"}>
								{t("waitlist.join.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className={"flex flex-col gap-4"}>
							<Button
								className={"w-full"}
								disabled={addToWaitlist.isPending}
								onClick={() => addToWaitlist.mutate()}
							>
								{addToWaitlist.isPending
									? t("waitlist.join.joining")
									: t("waitlist.join.joinButton")}
							</Button>
							<p className={"text-xs text-muted-foreground"}>
								{t("waitlist.join.noSpam")}
							</p>
						</CardContent>
					</>
				)}
			</Card>
		</Page>
	)
}

function WaitlistPushOption({ userId }: { userId: string }) {
	const { t } = useTranslation()
	const { isSupported, subscription, subscribe, unsubscribe } =
		usePushNotification({ userId })

	if (!isSupported) {
		return null
	}

	return subscription ? (
		<div className={"flex flex-col items-center gap-2"}>
			<div className={"flex items-center gap-2 text-sm text-primary"}>
				<BellRing className={"w-4 h-4"} />
				<span>{t("waitlist.push.notified")}</span>
			</div>
			<button
				className={
					"text-xs text-muted-foreground underline underline-offset-2 cursor-pointer"
				}
				onClick={unsubscribe}
			>
				{t("waitlist.push.turnOff")}
			</button>
		</div>
	) : (
		<div className={"flex flex-col items-center gap-2"}>
			<p className={"text-sm text-muted-foreground"}>
				{t("waitlist.push.prompt")}
			</p>
			<Button
				variant={"outline"}
				size={"sm"}
				className={"gap-2"}
				onClick={subscribe}
			>
				<Bell className={"w-4 h-4"} />
				{t("waitlist.push.enable")}
			</Button>
		</div>
	)
}
